import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, Layers, Search, Truck, X } from 'lucide-react';
import Sheet from './Sheet';
import { cn, daysFromToday, fmtDate, orderMatches, qty } from '../lib/utils';

/* Pick several purchase orders that arrived together and receive them as
   one. Search, tap to add or remove, start. */
const defaultLabel = (n) => (n === 0 ? 'Pick the orders' : n === 1 ? 'Open this order' : `Receive ${n} orders together`);

/* The order picker. Orders uses it to start a combined receipt; the delivery
   note reuses it with its own wording through title, subtitle and label. */
export default function PickOrdersSheet({ orders, initial = [], onClose, onStart, title = 'Receive together', subtitle = 'Add every order that arrived on this truck.', label = defaultLabel }) {
  const [query, setQuery] = useState('');
  // PO numbers are digits: the number pad by default, letters one tap away
  // for a supplier name. The choice sticks on this device.
  const [digits, setDigits] = useState(() => {
    try {
      return localStorage.getItem('inny.pickKeys') !== 'text';
    } catch {
      return true;
    }
  });
  const inputRef = useRef(null);
  const switchKeys = () => {
    const next = !digits;
    setDigits(next);
    try {
      localStorage.setItem('inny.pickKeys', next ? 'digits' : 'text');
    } catch {
      /* private mode */
    }
    // Android only swaps the keyboard on a fresh focus
    const el = inputRef.current;
    if (el) {
      el.blur();
      setTimeout(() => el.focus(), 60);
    }
  };
  const [selected, setSelected] = useState(() => new Set(initial));
  const [armed, setArmed] = useState(false); // adding a whole supplier takes a second tap
  useEffect(() => {
    if (!armed) return undefined;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);

  const byId = useMemo(() => Object.fromEntries(orders.map((o) => [o.id, o])), [orders]);
  const chosen = [...selected].map((id) => byId[id]).filter(Boolean);
  const suppliers = new Set(chosen.map((o) => o.supplier_id));

  const results = useMemo(() => {
    const searching = Boolean(query.trim());
    // With no search: the supplier(s) already chosen first, so the rest of the truck is one tap away.
    const list = orders.filter((o) => orderMatches(o, query));
    if (!searching && suppliers.size) {
      return [...list.filter((o) => suppliers.has(o.supplier_id)), ...list.filter((o) => !suppliers.has(o.supplier_id))].slice(0, 60);
    }
    return list.slice(0, 60);
  }, [orders, query, suppliers]);

  const matching = useMemo(() => orders.filter((o) => orderMatches(o, query)).length, [orders, query]);
  const sameSupplier = orders.filter((o) => suppliers.has(o.supplier_id) && !selected.has(o.id));

  const toggle = (id) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <Sheet open onClose={onClose} label="Receive several orders together" className="sheet-tall">
      {/* the sheet's drag handle takes 12px above this column */}
      <div className="flex flex-col px-4 pb-3 pt-3" style={{ height: 'calc(100% - 12px)' }}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="font-cond text-[24px] font-bold leading-none">{title}</p>
            <p className="mt-1 text-[14px] text-ink2">{subtitle}</p>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="-mr-2 -mt-1 flex h-10 w-10 items-center justify-center rounded-full active:bg-sunken">
            <X className="h-5 w-5" />
          </button>
        </div>

        <label className="mt-3 flex h-12 items-center gap-2 rounded-xl border border-line bg-surface px-3 focus-within:border-ink">
          <Search className="h-5 w-5 shrink-0 text-ink2" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder={digits ? 'PO number' : 'Supplier, PO number or reference'}
            inputMode={digits ? 'numeric' : 'search'}
            enterKeyHint="search"
            autoComplete="off"
            className="min-w-0 flex-1 bg-transparent text-[16px] outline-none placeholder:text-ink2/90"
          />
          <button type="button" onMouseDown={(e) => e.preventDefault()} onClick={switchKeys} aria-label={digits ? 'Letters keyboard' : 'Number keyboard'} className="h-8 shrink-0 rounded-lg bg-sunken px-2 text-[12px] font-bold text-ink2 active:bg-line">
            {digits ? 'ABC' : '123'}
          </button>
          {query && (
            <button type="button" onClick={() => setQuery('')} aria-label="Clear search" className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full active:bg-sunken">
              <X className="h-4 w-4" />
            </button>
          )}
        </label>

        {chosen.length > 0 && (
          // one row whatever the count: it scrolls sideways, so the list below never shrinks
          <div className="-mx-4 mt-2 flex shrink-0 items-center gap-1.5 overflow-x-auto px-4 pb-1">
            <span className="shrink-0 text-[12px] font-semibold text-ink2">{chosen.length} chosen</span>
            {sameSupplier.length > 0 && (
              <button type="button" onClick={() => {
                  if (sameSupplier.length > 1 && !armed) return setArmed(true);
                  setArmed(false);
                  return setSelected((prev) => new Set([...prev, ...sameSupplier.map((o) => o.id)]));
                }} className={cn('inline-flex h-10 shrink-0 items-center rounded-full px-3 text-[13px] font-semibold', armed ? 'bg-ink text-bg' : 'bg-sunken active:bg-line')}>
                {armed ? `Tap again to add all ${sameSupplier.length}` : `+ ${sameSupplier.length} more from ${suppliers.size === 1 ? chosen[0].supplier : 'the same suppliers'}`}
              </button>
            )}
            {chosen.map((o) => (
              <button key={o.id} type="button" onClick={() => toggle(o.id)} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full bg-ink pl-3 pr-2 text-[13px] font-semibold text-bg active:opacity-80" aria-label={`Remove ${o.name}`}>
                <span className="font-cond text-[15px]">{o.name.split(' ')[0]}</span>
                <X className="h-3.5 w-3.5" />
              </button>
            ))}
            <button type="button" onClick={() => setSelected(new Set())} className="inline-flex h-8 shrink-0 items-center rounded-full px-3 text-[13px] font-semibold text-ink2 active:bg-sunken">
              Clear
            </button>
          </div>
        )}

        <ul className="mt-2 min-h-0 flex-1 divide-y divide-line overflow-y-auto rounded-xl border border-line">
          {results.length === 0 && <li className="px-3 py-6 text-center text-[14px] text-ink2">{orders.length === 0 ? 'Loading open orders…' : `Nothing matches “${query}”.`}</li>}
          {results.map((o) => {
            const on = selected.has(o.id);
            const days = daysFromToday(o.expected);
            return (
              <li key={o.id}>
                <button type="button" onClick={() => toggle(o.id)} aria-pressed={on} className={cn('flex w-full items-center gap-3 px-3 py-2 text-left active:bg-sunken', on && 'bg-sunken')}>
                  <span className={cn('flex h-7 w-7 shrink-0 items-center justify-center rounded-full border-2', on ? 'border-ink bg-ink text-bg' : 'border-line')}>
                    {on && <Check className="h-4 w-4" strokeWidth={3} />}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="flex items-baseline gap-2">
                      <span className="font-cond truncate text-[18px] font-bold leading-none">{o.name}</span>
                      {o.shipped_pending && <Truck className="h-3.5 w-3.5 shrink-0 text-ink" />}
                    </span>
                    <span className="block truncate text-[13px] text-ink2">
                      {o.supplier} · {qty(o.qty_outstanding)} to receive{o.expected && ` · ${days !== null && days < 0 ? 'due ' : ''}${fmtDate(o.expected)}`}
                    </span>
                    {query.trim() && o.partner_ref && <span className="block truncate font-mono text-[12px] text-ink2">Vendor ref {o.partner_ref}</span>}
                  </span>
                </button>
              </li>
            );
          })}
          {matching > results.length && <li className="px-3 py-3 text-center text-[13px] text-ink2">{results.length} of {matching} shown. Type a number or name to find the others.</li>}
        </ul>

        <button
          type="button"
          onClick={() => onStart?.([...selected])}
          disabled={selected.size === 0}
          className="safe-b mt-3 flex h-14 w-full shrink-0 items-center justify-center gap-2 rounded-xl bg-ink text-[17px] font-semibold text-bg active:opacity-80 disabled:opacity-40"
        >
          <Layers className="h-5 w-5" /> {label(selected.size)}
        </button>
      </div>
    </Sheet>
  );
}
